import React, { useMemo, useCallback, PropsWithChildren } from 'react';

import type { ToastVariants, ToasterPush } from '@twilio-paste/core/toast';
import { Toaster, useToaster } from '@twilio-paste/core/toast';

import { createNullableContext, useNullableContext } from '../../../utils/Context';

interface ToasterContextProviderProps {
  dismissAfter?: number;
}

type ToastOptions = Omit<ToasterPush, 'message' | 'variant'>;

type ShowToastMessage = (message: string, options?: ToastOptions) => void;

export interface ToasterContextValue {
  showSuccess: ShowToastMessage;
  showWarning: ShowToastMessage;
  showError: ShowToastMessage;
  showInfo: ShowToastMessage;
}

export const ToasterContext = createNullableContext<ToasterContextValue>('ToasterContext');

export const useToasterContext = () => useNullableContext(ToasterContext);

const DEFAULT_DISMISS_TIME = 5000;

export const ToasterContextProvider = ({
  children,
  dismissAfter = DEFAULT_DISMISS_TIME,
}: PropsWithChildren<ToasterContextProviderProps>) => {
  const toaster = useToaster();

  const showToast = useCallback(
    (message: string, variant: ToastVariants, options?: ToastOptions) =>
      toaster.push({
        message,
        variant,
        dismissAfter,
        ...options,
      }),
    [toaster, dismissAfter],
  );

  const showSuccess = useCallback(
    (message: string, options?: ToastOptions) => showToast(message, 'success', options),
    [showToast],
  );

  const showWarning = useCallback(
    (message: string, options?: ToastOptions) => showToast(message, 'warning', options),
    [showToast],
  );

  const showError = useCallback(
    (message: string, options?: ToastOptions) => showToast(message, 'error', options),
    [showToast],
  );

  const showInfo = useCallback(
    (message: string, options?: ToastOptions) => showToast(message, 'neutral', options),
    [showToast],
  );

  const contextValue = useMemo<ToasterContextValue>(
    () => ({
      showError,
      showInfo,
      showSuccess,
      showWarning,
    }),
    [showError, showInfo, showSuccess, showWarning],
  );

  return (
    <ToasterContext.Provider value={contextValue}>
      {children}
      <Toaster {...toaster} />
    </ToasterContext.Provider>
  );
};
